import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { computeBudgetLive } from "./budgetCalcs";

export function ResumenFinancieroSection({ budgetId, items, pct, budget, readOnly, precioInputMode = "sin_iva" }) {
  const fmtPyg = (n) => `Gs. ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;

  // PDF REV1 A.2 / A.3: usar helper compartido — respeta % por grupo (override parcial).
  // Mantenemos el shape legacy { mat, mo, gm, ga, gmo, subtotalNeto, iva, total }.
  const live = useMemo(() => {
    const r = computeBudgetLive(items, pct, precioInputMode);
    return {
      mat: r.mat_directo,
      mo: r.mo_directo,
      gm: r.gm,
      ga: r.ga,
      gmo: r.gmo,
      subtotalNeto: r.subtotal_neto,
      iva: r.iva,
      total: r.total,
    };
  }, [items, pct, precioInputMode]);

  const [retencionPct, setRetencionPct] = useState(budget?.retencion_pct ?? 5.0);
  const [gananciaContPct, setGananciaContPct] = useState(budget?.ganancia_contratista_pct ?? 0.0);
  const [costosOp, setCostosOp] = useState({
    honorarios: budget?.costos_operativos?.honorarios || 0,
    movilidad: budget?.costos_operativos?.movilidad || 0,
    factor_obras: budget?.costos_operativos?.factor_obras ?? 100,
  });

  // Certificado: campos preservados en backend para compatibilidad (B.6 ocultó
  // las secciones "RESUMEN DEL CERTIFICADO" y "ANÁLISIS DE GARANTÍAS", pero
  // seguimos serializando los valores para no romper budgets legacy).
  const certGI = budget?.cert_gastos_indirectos_pct ?? 0;
  const certGA = budget?.cert_gastos_admin_pct ?? 0;
  const certGarantiaCliente = budget?.cert_garantia_cliente_pct ?? 10;
  const certRetencion = budget?.cert_retencion_pct ?? 5;
  const certRetencionTrans = budget?.cert_retencion_trans_pct ?? 3;

  const [saving, setSaving] = useState(false);

  const meses = parseFloat(budget?.hh_meses_duracion || 1) || 1;
  const totalGanancias = live.gm + live.gmo;
  const retencion = live.total * (parseFloat(retencionPct) || 0) / 100;
  const gananciasMenosRet = totalGanancias - retencion;
  const mensual = meses > 0 ? gananciasMenosRet / meses : 0;
  // Excel CEN: Ganancia Contratista se calcula sobre Costo de Mano de Obra (no Subtotal Neto)
  const gananciaContr = live.mo * (parseFloat(gananciaContPct) || 0) / 100;
  const gananciaContrTotal = gananciaContr * 1.10;
  const coMensual = (parseFloat(costosOp.honorarios) || 0) + (parseFloat(costosOp.movilidad) || 0);
  const coAnual = coMensual * 13;
  const coFinObra = coAnual * (parseFloat(costosOp.factor_obras) ?? 100) / 100;

  // Excel CEN — RESUMEN extendido
  const gastosAdminTotal = (budget?.gastos_admin_items || []).reduce((s, i) => s + (parseFloat(i.monto) || 0), 0);
  const totalCostoCEN = live.mat + live.mo + gastosAdminTotal;       // TOTAL COSTO (Mat + MO + GA)
  const ivaCEN = live.iva;                                            // IVA del cómputo

  // Certificado calculations — solo persistencia, no se renderizan (B.6).
  // Mantenemos los valores guardados intactos al serializar.

  const save = async () => {
    if (!budgetId) { toast.error("Guardá el presupuesto primero"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${budgetId}`, {
        retencion_pct: parseFloat(retencionPct) || 5.0,
        ganancia_contratista_pct: parseFloat(gananciaContPct) || 0.0,
        costos_operativos: {
          honorarios: parseFloat(costosOp.honorarios) || 0,
          movilidad: parseFloat(costosOp.movilidad) || 0,
          factor_obras: parseFloat(costosOp.factor_obras) ?? 100,
        },
        cert_gastos_indirectos_pct: parseFloat(certGI) || 0,
        cert_gastos_admin_pct: parseFloat(certGA) || 0,
        cert_garantia_cliente_pct: parseFloat(certGarantiaCliente) || 10,
        cert_retencion_pct: parseFloat(certRetencion) || 5,
        cert_retencion_trans_pct: parseFloat(certRetencionTrans) || 3,
      }, { withCredentials: true });
      toast.success("Resumen financiero guardado");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  // PDF REV1 — % EFECTIVO en etiquetas del Resumen.
  // Si el costo base es 0, mostramos el % global. Si hay costo y por ejemplo hay overrides por grupo,
  // el % efectivo se deriva como gm/mat × 100 — refleja exactamente lo que se está calculando.
  const effGmPct = live.mat > 0 ? (live.gm / live.mat) * 100 : (parseFloat(pct.ganancia_mat_pct) || 10);
  const effGaPct = (live.mat + live.mo) > 0 ? (live.ga / (live.mat + live.mo)) * 100 : (parseFloat(pct.ga_pct) || 10);
  const effGmoPct = live.mo > 0 ? (live.gmo / live.mo) * 100 : (parseFloat(pct.gmo_pct) || 45);
  const fmtPct = (n) => `${Math.round(n * 10) / 10}%`;
  // FIX 2 — % del SUBTOTAL NETO (distinto de la "tasa" aplicada). Aclara la nomenclatura.
  const pctOfSub = (x) => (live.subtotalNeto > 0 ? (x / live.subtotalNeto) * 100 : 0);

  const SumRow = ({ label, value, highlight, neg, indent, sub }) => (
    <tr className={`border-t border-gray-50 ${highlight ? "bg-[#EBF3FB]" : ""}`}>
      <td className={`px-3 py-2 text-xs ${indent ? "pl-6 text-gray-500" : "font-medium text-gray-700"} ${highlight ? "font-bold text-[#1A4B7A]" : ""}`}>
        {label}
        {sub && <span className="block text-[10px] text-gray-400 font-normal">{sub}</span>}
      </td>
      <td className={`px-3 py-2 text-right font-mono text-xs ${highlight ? "font-bold text-[#1A4B7A]" : neg ? "text-red-600 font-medium" : "text-gray-700"}`}>{neg ? "- " : ""}{value}</td>
    </tr>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="resumen-financiero-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#2E6DA4]/10 flex items-center justify-center">
            <FileText size={13} className="text-[#2E6DA4]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Resumen de Presupuesto</h3>
            <p className="text-[11px] text-gray-400">Desglose financiero completo · RETENCIÓN · Ganancia Contratista · Costos Operativos</p>
          </div>
        </div>
        {!readOnly && (
          <button onClick={save} disabled={saving || !budgetId}
            className="flex items-center gap-1 text-xs text-white bg-[#2E6DA4] hover:bg-[#245A8A] px-3 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
            data-testid="save-resumen-btn">
            {saving ? <Loader2 size={12} className="animate-spin" /> : null} Guardar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Desglose costos */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-[#1A4B7A] text-white px-3 py-2">
            <p className="text-xs font-bold tracking-wide">DESGLOSE DEL COSTO</p>
          </div>
          <table className="w-full">
            <tbody>
              <SumRow label="Costo de Materiales" value={fmtPyg(live.mat)} />
              <SumRow label="Costo de Mano de Obra" value={fmtPyg(live.mo)} />
              <SumRow label={`Ganancia Materiales (tasa ${fmtPct(effGmPct)})`} sub={`${fmtPct(pctOfSub(live.gm))} del subtotal`} value={fmtPyg(live.gm)} indent />
              <SumRow label={`Gastos Administrativos (tasa ${fmtPct(effGaPct)})`} sub={`${fmtPct(pctOfSub(live.ga))} del subtotal`} value={fmtPyg(live.ga)} indent />
              <SumRow label={`Ganancia M.O. (tasa ${fmtPct(effGmoPct)})`} sub={`${fmtPct(pctOfSub(live.gmo))} del subtotal`} value={fmtPyg(live.gmo)} indent />
              <tr className="bg-gray-50 border-t border-gray-200">
                <td className="px-3 py-2 text-xs font-bold text-gray-700">TOTAL COSTO (Mat + M.O. + Gastos Adm.)</td>
                <td className="px-3 py-2 text-right text-xs font-bold text-gray-800 font-mono">{fmtPyg(totalCostoCEN)}</td>
              </tr>
              <tr className="bg-[#1A4B7A]">
                <td className="px-3 py-2 text-xs font-bold text-white">SUBTOTAL NETO</td>
                <td className="px-3 py-2 text-right text-xs font-bold text-white font-mono">{fmtPyg(live.subtotalNeto)}</td>
              </tr>
              <SumRow label="IVA CEN (10%)" value={fmtPyg(ivaCEN)} indent />
              <tr className="bg-[#2E6DA4]">
                <td className="px-3 py-2 text-xs font-bold text-white">TOTAL IVA INCLUIDO</td>
                <td className="px-3 py-2 text-right text-xs font-bold text-white font-mono">{fmtPyg(live.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Ganancias y Retención */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-emerald-700 text-white px-3 py-2">
            <p className="text-xs font-bold tracking-wide">ANÁLISIS DE GANANCIAS</p>
          </div>
          <table className="w-full">
            <tbody>
              <SumRow label="Total Ganancias (G.Mat + G.MO)" value={fmtPyg(totalGanancias)} highlight />
              <tr className="border-t border-gray-50">
                <td className="px-3 py-2">
                  <span className="text-xs font-medium text-gray-700">Retención del Cliente</span>
                  {!readOnly ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <input type="number" min="0" max="100" step="0.5" value={retencionPct}
                        onChange={e => setRetencionPct(e.target.value)}
                        className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs text-center focus:ring-1 focus:ring-emerald-400"
                        data-testid="retencion-pct-input" />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  ) : <span className="text-xs text-gray-400 block mt-0.5">{retencionPct}%</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs text-red-600 font-medium">- {fmtPyg(retencion)}</td>
              </tr>
              <tr className="bg-emerald-50 border-t border-emerald-100">
                <td className="px-3 py-2 text-xs font-bold text-emerald-800">GANANCIAS − RETENCIÓN</td>
                <td className="px-3 py-2 text-right font-mono text-xs font-bold text-emerald-800">{fmtPyg(gananciasMenosRet)}</td>
              </tr>
              <tr className="border-t border-gray-50">
                <td className="px-3 py-2 text-xs text-gray-600">Mensual <span className="text-gray-400">÷ {meses} {meses === 1 ? "mes" : "meses"}</span></td>
                <td className="px-3 py-2 text-right font-mono text-xs font-medium text-emerald-700">{fmtPyg(mensual)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* GANANCIA DE CONTRATISTA */}
      <div className="rounded-xl border border-amber-200 overflow-hidden mb-4">
        <div className="bg-amber-600 text-white px-3 py-2">
          <p className="text-xs font-bold tracking-wide">GANANCIA DE CONTRATISTA</p>
        </div>
        <div className="p-3 bg-amber-50/40">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-amber-700 mb-1">% sobre Costo de M.O.</label>
              {readOnly ? (
                <p className="text-sm font-bold text-gray-800">{gananciaContPct}%</p>
              ) : (
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="100" step="0.1" value={gananciaContPct}
                    onChange={e => setGananciaContPct(e.target.value)}
                    className="w-20 border border-amber-300 rounded-lg px-2 py-1 text-sm focus:ring-1 focus:ring-amber-300"
                    data-testid="ganancia-contratista-pct-input" />
                  <span className="text-sm text-amber-700 font-bold">%</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold text-amber-700 mb-1">Ganancia Mensual</label>
              <p className="text-sm font-bold text-amber-700 font-mono">{fmtPyg(meses > 0 ? gananciaContr / meses : 0)}</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-amber-700 mb-1">Total + IVA</label>
              <p className="text-base font-bold text-amber-800 font-mono">{fmtPyg(gananciaContrTotal)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* COSTOS OPERATIVOS */}
      <div className="rounded-xl border border-violet-200 overflow-hidden">
        <div className="bg-violet-700 text-white px-3 py-2">
          <p className="text-xs font-bold tracking-wide">COSTOS OPERATIVOS</p>
        </div>
        <div className="p-3 bg-violet-50/30">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            <div>
              <label className="block text-[10px] font-bold text-violet-700 mb-1">Honorarios / mes</label>
              {readOnly ? <p className="text-xs font-mono">{fmtPyg(costosOp.honorarios)}</p> : (
                <input type="number" min="0" value={costosOp.honorarios}
                  onChange={e => setCostosOp(p => ({ ...p, honorarios: e.target.value }))}
                  className="w-full border border-violet-300 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-violet-300"
                  data-testid="costos-honorarios-input" />
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold text-violet-700 mb-1">Movilidad + servicios / mes</label>
              {readOnly ? <p className="text-xs font-mono">{fmtPyg(costosOp.movilidad)}</p> : (
                <input type="number" min="0" value={costosOp.movilidad}
                  onChange={e => setCostosOp(p => ({ ...p, movilidad: e.target.value }))}
                  className="w-full border border-violet-300 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-violet-300"
                  data-testid="costos-movilidad-input" />
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold text-violet-700 mb-1">MENSUAL</label>
              <p className="text-xs font-bold text-violet-800 font-mono">{fmtPyg(coMensual)}</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-violet-700 mb-1">12 Meses + Aguinaldo</label>
              <p className="text-xs font-bold text-violet-800 font-mono">{fmtPyg(coAnual)}</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-violet-700 mb-1">Factor Obras (%)</label>
              {readOnly ? <p className="text-xs">{costosOp.factor_obras}%</p> : (
                <div className="flex items-center gap-1">
                  <input type="number" min="0" max="100" step="5" value={costosOp.factor_obras}
                    onChange={e => setCostosOp(p => ({ ...p, factor_obras: e.target.value }))}
                    className="w-16 border border-violet-300 rounded-lg px-2 py-1 text-xs focus:ring-1 focus:ring-violet-300"
                    data-testid="costos-factor-obras-input" />
                  <span className="text-xs text-violet-600">%</span>
                </div>
              )}
            </div>
          </div>
          <div className="bg-violet-100 rounded-lg px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-bold text-violet-800">FIN DE OBRA (costo op. total)</span>
            <span className="text-base font-bold text-violet-900 font-mono">{fmtPyg(coFinObra)}</span>
          </div>
        </div>
      </div>

      {/* B.6 — Resumen del Certificado / Análisis de Garantías removidos
          (no aplican según PDF REV4). Los campos cert_* del modelo se preservan
          en backend para compatibilidad con presupuestos existentes. */}
    </div>
  );
}

